import { useCallback, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import followsApi from "@/api/follows/client";
import useSignedFunction from "@/shared/hooks/useSignedFunction";
import useMe from "./useMe";

const useFollow = ({
  userId,
  isMeFollowing = false,
}: {
  userId?: string;
  isMeFollowing: boolean;
}) => {
  const [isFollowing, setIsFollowing] = useState(isMeFollowing);
  const queryClient = useQueryClient();

  const { me } = useMe();

  useEffect(() => {
    setIsFollowing(isMeFollowing);
  }, [isMeFollowing]);

  const { mutateAsync } = useMutation({
    mutationFn: (foloweeId: string) => followsApi.toggleFollow(foloweeId),
    onSuccess: () => {
      setIsFollowing((prevState) => !prevState);
      queryClient.invalidateQueries(["artist-followers", userId]);
      queryClient.invalidateQueries(["followers-count"]);
      queryClient.invalidateQueries(["me"]);
      queryClient.invalidateQueries(["completed-quests", me?.id]);
      queryClient.invalidateQueries(["uncompleted-quests", me?.id]);
    },
  });

  const toggleFollow = useCallback(async () => {
    userId && (await mutateAsync(userId));
  }, [mutateAsync, userId]);

  const checkUserAndToggleFollow = useSignedFunction(toggleFollow);

  return {
    isFollowing,
    toggleFollow: checkUserAndToggleFollow,
  };
};

export default useFollow;
